/**
 * 🔇 Observer Read Service — Mute local de tickets visualizados como observador
 *
 * Quando um atendente que NÃO é o dono do ticket (admin, supervisor, teammate)
 * abre um ticket, o backend mantém `unreadMessages > 0` no banco (regra
 * "lido por dono"). Para que esse atendente também tenha feedback visual de
 * "já vi", mantemos um Set local em localStorage com os ticketIds que ele
 * "consumiu" como observador.
 *
 * Comportamento:
 * - add(ticketId): atendente abriu o ticket sem ser dono → silencia visual
 *   localmente (badge zera, team alert some).
 * - remove(ticketId): chegou nova mensagem com o ticket NÃO em foco →
 *   reativa visual.
 * - has(ticketId): consulta para o render.
 * - clear(): logout / troca de empresa (reseta memória/listener; por padrão
 *   preserva o localStorage para o mesmo usuário voltar sem perder o "já vi").
 *
 * Persistência: localStorage por (userId, companyId), debounced 200ms.
 * Limite: 500 entries (FIFO) para evitar leak.
 * Multi-aba: sync via storage event listener.
 */

const STORAGE_PREFIX = "omniplay_observer_read";
const SAVE_DEBOUNCE_MS = 200;
const MAX_ENTRIES = 500;

class ObserverReadService {
  constructor() {
    this.userId = null;
    this.companyId = null;
    this.set = new Set();
    this.listeners = new Set();
    this._storageListener = null;
    this._saveTimeout = null;
  }

  init(userId, companyId) {
    if (
      userId === undefined ||
      userId === null ||
      companyId === undefined ||
      companyId === null
    ) {
      return;
    }
    if (this.userId === userId && this.companyId === companyId) {
      return;
    }
    this.userId = userId;
    this.companyId = companyId;
    this._loadFromStorage();

    if (this._storageListener) {
      try {
        window.removeEventListener("storage", this._storageListener);
      } catch {
        // ignore
      }
    }
    this._storageListener = (e) => {
      if (e.key === this._storageKey()) {
        this._loadFromStorage();
        this._notify();
      }
    };
    try {
      window.addEventListener("storage", this._storageListener);
    } catch {
      // ambiente sem window (SSR/teste)
    }
  }

  _storageKey() {
    return `${STORAGE_PREFIX}_${this.userId}_${this.companyId}`;
  }

  _loadFromStorage() {
    try {
      const raw = window.localStorage.getItem(this._storageKey());
      const arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) {
        this.set = new Set();
        return;
      }
      this.set = new Set(
        arr.filter((x) => typeof x === "number" && Number.isFinite(x))
      );
    } catch {
      this.set = new Set();
    }
  }

  _saveToStorage() {
    if (this._saveTimeout) {
      clearTimeout(this._saveTimeout);
    }
    this._saveTimeout = setTimeout(() => {
      this._saveTimeout = null;
      try {
        const arr = Array.from(this.set);
        if (arr.length > MAX_ENTRIES) {
          // Manter os mais recentes (FIFO simples — o Set preserva ordem de inserção)
          const trimmed = arr.slice(arr.length - MAX_ENTRIES);
          this.set = new Set(trimmed);
          window.localStorage.setItem(
            this._storageKey(),
            JSON.stringify(trimmed)
          );
        } else {
          window.localStorage.setItem(this._storageKey(), JSON.stringify(arr));
        }
      } catch {
        // localStorage indisponível ou quota excedida — cai silenciosamente
      }
    }, SAVE_DEBOUNCE_MS);
  }

  add(ticketId) {
    const id = Number(ticketId);
    if (!Number.isFinite(id)) return;
    if (this.set.has(id)) return;
    this.set.add(id);
    this._saveToStorage();
    this._notify();
  }

  remove(ticketId) {
    const id = Number(ticketId);
    if (!Number.isFinite(id)) return;
    if (!this.set.has(id)) return;
    this.set.delete(id);
    this._saveToStorage();
    this._notify();
  }

  has(ticketId) {
    const id = Number(ticketId);
    if (!Number.isFinite(id)) return false;
    return this.set.has(id);
  }

  clear({ removeStorage = false } = {}) {
    if (this.set.size === 0 && !this.userId) return;
    this.set.clear();
    if (this._saveTimeout) {
      clearTimeout(this._saveTimeout);
      this._saveTimeout = null;
    }
    if (removeStorage && this.userId !== null && this.companyId !== null) {
      try {
        window.localStorage.removeItem(this._storageKey());
      } catch {
        // ignore
      }
    }
    if (this._storageListener) {
      try {
        window.removeEventListener("storage", this._storageListener);
      } catch {
        // ignore
      }
      this._storageListener = null;
    }
    this.userId = null;
    this.companyId = null;
    this._notify();
  }

  subscribe(listener) {
    if (typeof listener !== "function") return () => {};
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  _notify() {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch {
        // listener individual com erro não derruba os demais
      }
    });
  }
}

const observerReadService = new ObserverReadService();
export default observerReadService;
